<template>
    <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <!-- Sidebar -->
      <HrSidebar :isOpen="isSidebarOpen" />
  
      <!-- Main Content -->
      <main 
        class="flex-1 overflow-x-hidden transition-all duration-300"
        :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
      >
        <!-- Navbar -->
        <header 
          class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm transition-all duration-300"
        >
          <HrTopbar 
            :page-title="currentPageTitle"
            :is-sidebar-open="isSidebarOpen"
            :is-dark-mode="isDarkMode"
            @toggle-sidebar="toggleSidebar"
            @toggle-dark-mode="toggleDarkMode"
          />
        </header>
        <div class="p-6 max-w-full">

        <!-- Applicant Information Card -->
        <div v-if="applicantInfo" class="mt-20 mb-6 bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
          <div class="border-l-4 border-[#74bd54] p-5">
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <div class="flex justify-between items-start">
                  <div>
                    <h2 class="text-xl font-bold text-gray-800 dark:text-white">
                      Applicant: {{ applicantInfo.full_name || 'Not Available' }}
                    </h2>
                    <p class="text-gray-600 dark:text-gray-300 mt-1">
                      Position: {{ applicantInfo.position_title || 'Not specified' }}
                    </p>
                  </div>
                  <div class="text-right">
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      Status: 
                      <span :class="getStatusColor(applicantInfo.applicant_status)" class="font-semibold">
                        {{ applicantInfo.applicant_status || 'Pending' }}
                      </span>
                    </p>
                    <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Entry Status: {{ applicantInfo.entry_status || 'N/A' }}
                    </p>
                  </div>
                </div>
                
                <div class="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <p class="text-sm text-gray-500 dark:text-gray-400">Email</p>
                    <p class="text-gray-800 dark:text-white">{{ applicantInfo.email || 'Not available' }}</p>
                  </div>
                  <div>
                    <p class="text-sm text-gray-500 dark:text-gray-400">Phone</p>
                    <p class="text-gray-800 dark:text-white">{{ applicantInfo.phone || 'Not available' }}</p>
                  </div>
                  <div>
                    <p class="text-sm text-gray-500 dark:text-gray-400">Period</p>
                    <p class="text-gray-800 dark:text-white">{{ applicantInfo.period || 'Not specified' }}</p>
                  </div>
                  <div>
                    <p class="text-sm text-gray-500 dark:text-gray-400">Date Shortlisted</p>
                    <p class="text-gray-800 dark:text-white">{{ formatDate(applicantInfo.created_at) }}</p>
                  </div>
                </div>

                <!-- Interview Schedule -->
                <div v-if="interviewSchedule && interviewSchedule.interview_date" class="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h3 class="font-semibold text-blue-800 dark:text-blue-300 mb-2">Interview Schedule</h3>
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <p class="text-sm text-gray-500 dark:text-gray-400">Date</p>
                      <p class="text-gray-800 dark:text-white font-medium">{{ formatDate(interviewSchedule.interview_date) }}</p>
                    </div>
                    <div>
                      <p class="text-sm text-gray-500 dark:text-gray-400">Time</p>
                      <p class="text-gray-800 dark:text-white font-medium">{{ interviewSchedule.interview_time }}</p>
                    </div>
                    <div v-if="interviewSchedule.notes" class="col-span-2">
                      <p class="text-sm text-gray-500 dark:text-gray-400">Notes</p>
                      <p class="text-gray-800 dark:text-white">{{ interviewSchedule.notes }}</p>
                    </div>
                  </div>
                </div>

                <!-- CV Section from applicant_cv table -->
                <div v-if="applicantCVs && applicantCVs.length > 0" class="mt-4">
                  <h3 class="font-semibold text-gray-700 dark:text-gray-300 mb-2">CV / Documents ({{ applicantCVs.length }})</h3>
                  <div class="flex flex-wrap gap-2">
                    <div v-for="(cv, index) in applicantCVs" :key="cv.id || index" class="flex gap-2">
                      <button 
                        @click="viewCV(cv.file_path)"
                        class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-sm transition"
                      >
                        <i class="fas fa-eye mr-1"></i> View CV {{ applicantCVs.length > 1 ? index + 1 : '' }}
                      </button>
                      <button 
                        @click="downloadCV(cv.file_path)"
                        class="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-lg text-sm transition"
                      >
                        <i class="fas fa-download mr-1"></i> Download
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Single CV from applicants table -->
                <div v-else-if="applicantInfo.cv_file_path" class="mt-4">
                  <h3 class="font-semibold text-gray-700 dark:text-gray-300 mb-2">CV / Document</h3>
                  <div class="flex gap-2">
                    <button 
                      @click="viewCV(applicantInfo.cv_file_path)"
                      class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-sm transition"
                    >
                      <i class="fas fa-eye mr-1"></i> View CV
                    </button>
                    <button 
                      @click="downloadCV(applicantInfo.cv_file_path)"
                      class="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-lg text-sm transition"
                    >
                      <i class="fas fa-download mr-1"></i> Download CV
                    </button>
                  </div>
                </div>

            <!-- CV Section from applicant_cv table -->
            <div v-if="applicantCVs && applicantCVs.length > 0" class="mt-4">
              <h3 class="font-semibold text-gray-700 dark:text-gray-300 mb-2">CV / Documents ({{ applicantCVs.length }})</h3>
              <div class="flex flex-wrap gap-2">
                <div v-for="(cv, index) in applicantCVs" :key="cv.id || index" class="flex gap-2">
                  <button @click="viewCV(cv.file_path)" class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-sm transition">
                    <i class="fas fa-eye mr-1"></i> View CV {{ applicantCVs.length > 1 ? index + 1 : '' }}
                  </button>
                  <button @click="downloadCV(cv.file_path)" class="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-lg text-sm transition">
                    <i class="fas fa-download mr-1"></i> Download
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Single CV from applicants table -->
            <div v-else-if="applicantInfo.cv_file_path" class="mt-4">
              <h3 class="font-semibold text-gray-700 dark:text-gray-300 mb-2">CV / Document</h3>
              <div class="flex gap-2">
                <button @click="viewCV(applicantInfo.cv_file_path)" class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-sm transition">
                  <i class="fas fa-eye mr-1"></i> View CV
                </button>
                <button @click="downloadCV(applicantInfo.cv_file_path)" class="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-lg text-sm transition">
                  <i class="fas fa-download mr-1"></i> Download CV
                </button>
              </div>
            </div>
            
            <!-- ✅ Attachments section (INDEPENDENT - not in v-else chain) -->
            <div v-if="applicantInfo.attachments && applicantInfo.attachments.length > 0" class="mt-4">
              <h3 class="font-semibold text-gray-700 dark:text-gray-300 mb-2">Pre-Interview Documents ({{ applicantInfo.attachments.length }})</h3>
              <div class="space-y-2">
                <div 
                  v-for="(att, index) in applicantInfo.attachments" 
                  :key="att.id || index"
                  class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                >
                  <div class="flex items-center gap-2 min-w-0">
                    <span class="text-lg flex-shrink-0">{{ getFileIcon(att.file_type) }}</span>
                    <span class="text-sm text-gray-700 dark:text-gray-300 truncate" :title="att.file_name">
                      {{ att.file_name || att.file_path.split('/').pop() }}
                    </span>
                  </div>
                  <div class="flex gap-2 flex-shrink-0">
                    <button @click="viewCV(att.file_path)" class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-sm transition">
                      <i class="fas fa-eye mr-1"></i> View
                    </button>
                    <button @click="downloadCV(att.file_path)" class="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-lg text-sm transition">
                      <i class="fas fa-download mr-1"></i> Download
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- No Documents Message (only if NO cv AND NO attachments) -->
            <div v-if="!applicantInfo.cv_file_path && (!applicantInfo.attachments || applicantInfo.attachments.length === 0)" class="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p class="text-sm text-gray-500 dark:text-gray-400">No documents uploaded yet</p>
            </div>

                <!-- No CV Message -->
                <div v-else class="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p class="text-sm text-gray-500 dark:text-gray-400">No CV uploaded yet</p>
                </div>

                <!-- Recommendation Info -->
                <div v-if="applicantInfo.recommendation_type" class="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <h3 class="font-semibold text-gray-700 dark:text-gray-300 mb-2">Recommendation</h3>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    Recommended By: {{ applicantInfo.recommendation_type }}
                    <span v-if="applicantInfo.recommendation_id"> | Name: {{ applicantInfo.recommendation_name }}</span>
                  </p>
                  <p v-if="applicantInfo.recommendation_text" class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {{ applicantInfo.recommendation_text }}
                  </p>
                </div>

                <!-- Applicant Comment -->
                <div v-if="applicantInfo.applicant_comment" class="mt-4">
                  <h3 class="font-semibold text-gray-700 dark:text-gray-300 mb-2">Applicant Comment</h3>
                  <p class="text-sm text-gray-600 dark:text-gray-400">{{ applicantInfo.applicant_comment }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
          <div v-if="!applicantInfo" class="mt-20 flex justify-center items-center">
          <div class="text-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#74bd54] mx-auto"></div>
            <p class="mt-4 text-gray-600 dark:text-gray-400">Loading applicant information...</p>
          </div>
        </div>

        <!-- Interview Evaluation Criteria Section -->
        <div v-if="evaluationCriteria.length > 0" class="mb-6 bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
          <div class="border-l-4 border-[#74bd54] p-5">
            <h3 class="text-lg font-bold text-gray-800 dark:text-white mb-4">Interview Evaluation Criteria</h3>

            <div class="space-y-6">
              <div v-for="criteria in evaluationCriteria" :key="criteria.id" class="border-b dark:border-gray-700 pb-6 last:border-0">
                <div class="mb-3">
                  <h4 class="font-semibold text-gray-800 dark:text-white">{{ criteria.name }}</h4>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">Rate from 1 to {{ criteria.max_score || 10 }}</p>
                </div>

                <!-- Radio Button Rating Group -->
                <div class="mb-3">
                  <div class="flex flex-wrap gap-2">
                    <label 
                      v-for="score in generateRatingOptions(criteria.max_score || 10)" 
                      :key="score" 
                      class="relative flex items-center justify-center cursor-pointer"
                    >
                      <input 
                        type="radio"
                        :value="score"
                        v-model="evaluationRatings[criteria.id]"
                        class="sr-only peer"
                        :name="`rating_${criteria.id}`"
                      />
                      <div class="w-10 h-10 rounded-full border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center text-sm font-medium text-gray-700 dark:text-gray-300 peer-checked:bg-[#74bd54] peer-checked:border-[#74bd54] peer-checked:text-white transition-all duration-200 hover:border-[#74bd54] hover:scale-105">
                        {{ score }}
                      </div>
                    </label>
                  </div>
                </div>

                <!-- Comments for this criteria -->
                <div class="mt-3">
                  <textarea 
                    v-model="evaluationComments[criteria.id]"
                    placeholder="Add specific comments for this criteria..."
                    class="w-full border rounded-lg p-2 text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54]"
                    rows="2"
                  ></textarea>
                </div>
              </div>
            </div>

            <!-- Overall Interview Comments -->
            <div class="mt-6 pt-4 border-t dark:border-gray-700">
              <label class="block font-semibold text-gray-800 dark:text-white mb-2">Overall Interview Comments</label>
              <textarea 
                v-model="overallComments"
                placeholder="Provide overall feedback about the candidate..."
                class="w-full border rounded-lg p-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54]"
                rows="3"
              ></textarea>
            </div>

            <!-- Recommendation Decision -->
            <div class="mt-4">
              <label class="block font-semibold text-gray-800 dark:text-white mb-2">Recommendation</label>
              <div class="flex gap-6">
                <label v-if="hasPermission('manpower', 'validate')" class="flex items-center cursor-pointer">
                  <input  type="radio" v-model="recommendation" value="Validated" class="w-4 h-4 text-[#74bd54] focus:ring-[#74bd54] mr-2">
                  <span class="text-green-600 dark:text-green-400 font-medium">Validate</span>
                </label>
                <label v-if="hasPermission('manpower', 'second_interview')" class="flex items-center cursor-pointer">
                  <input type="radio" v-model="recommendation" value="second_interview" class="w-4 h-4 text-yellow-700 focus:ring-[#74bd54] mr-2">
                  <span class="text-yellow-700 dark:text-green-400 font-medium">Second Interview</span>
                </label>
                <label v-if="hasPermission('manpower', 'consider')"  class="flex items-center cursor-pointer">
                  <input type="radio" v-model="recommendation" value="consider" class="w-4 h-4 text-yellow-500 focus:ring-yellow-500 mr-2">
                  <span class="text-yellow-600 dark:text-yellow-400 font-medium">Consider for another position</span>
                </label>
                <label v-if="hasPermission('manpower', 'reject')" class="flex items-center cursor-pointer">
                  <input  type="radio" v-model="recommendation" value="reject" class="w-4 h-4 text-red-500 focus:ring-red-500 mr-2">
                  <span class="text-red-600 dark:text-red-400 font-medium">Reject</span>
                </label>
              </div>
            </div>

            <!-- Optional: Show selected ratings summary -->
            <div v-if="hasSelectedRatings" class="mt-6 pt-4 border-t dark:border-gray-700">
              <div class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <h4 class="font-semibold text-gray-700 dark:text-gray-300 mb-2">Rating Summary</h4>
                <div class="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <div v-for="criteria in evaluationCriteria" :key="criteria.id" class="text-sm">
                    <span class="text-gray-600 dark:text-gray-400">{{ criteria.name }}:</span>
                    <span class="font-semibold ml-2 text-gray-800 dark:text-white">
                      {{ evaluationRatings[criteria.id] || 'Not rated' }}
                      <span v-if="evaluationRatings[criteria.id]" class="text-xs text-gray-500">/{{ criteria.max_score || 10 }}</span>
                    </span>
                  </div>
                </div>
                <div v-if="calculateTotalScore()" class="mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                  <div class="flex justify-between items-center">
                    <span class="font-semibold text-gray-700 dark:text-gray-300">Total Score:</span>
                    <span class="font-bold text-lg text-[#74bd54]">{{ calculateTotalScore() }}</span>
                  </div>
                  <div class="flex justify-between items-center mt-1">
                    <span class="font-semibold text-gray-700 dark:text-gray-300">Average Score:</span>
                    <span class="font-bold text-lg text-[#74bd54]">{{ calculateAverageScore() }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Questions List -->
        <div v-if="mainQuestions.length > 0" class="space-y-6">
          <div v-for="main in mainQuestions" :key="main.id" class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow">
            <h3 class="font-semibold text-gray-800 dark:text-white mb-2">{{ main.question_text }}</h3>
            <p class="text-xs text-gray-500 mb-4">{{ main.category }}</p>

            <!-- Main Answer -->
            <div class="mb-4">
              <textarea v-model="answers[main.id].answer"
                        placeholder="Enter answer here..."
                        class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
                        rows="3"></textarea>
            </div>

            <!-- Subquestions -->
            <div v-if="subQuestions[main.id]?.length" class="ml-4 space-y-3">
              <div v-for="sub in subQuestions[main.id]" :key="sub.id">
                <p class="text-sm text-gray-700 dark:text-gray-300 mb-1">{{ sub.question_text }}</p>
                <textarea v-model="answers[sub.id].answer"
                          placeholder="Enter answer here..."
                          class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
                          rows="2"></textarea>
              </div>
            </div>

            <!-- Notes / Remarks -->
            <div class="mt-4">
              <input type="text"
                     v-model="answers[main.id].notes"
                     placeholder="Add notes or remarks..."
                     class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white" />
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="mt-6 flex gap-4 justify-end">
  <button 
  @click="goBack"
  type="button"
  class="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 shadow transition flex items-center gap-2"
>
  <i class="fas fa-arrow-left"></i>
  <span>Back</span>
</button>
 <button v-if="hasPermission('manpower', 'submit_interview')" 
 @click="submitInterview"
        :disabled="isSubmitting"
        class="bg-[#74bd54] text-white px-6 py-2 rounded-lg hover:bg-[#63a43f] shadow disabled:opacity-50 disabled:cursor-not-allowed">
        <svg 
    v-if="isLoading" 
    class="animate-spin h-4 w-4 text-white" 
    xmlns="http://www.w3.org/2000/svg" 
    fill="none" 
    viewBox="0 0 24 24"
  >
    <circle 
      class="opacity-25" 
      cx="12" 
      cy="12" 
      r="10" 
      stroke="currentColor" 
      stroke-width="4"
    ></circle>
    <path 
      class="opacity-75" 
      fill="currentColor" 
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
<span>
  {{ (isSubmitting || isLoading)
      ? (isEditMode ? 'Updating...' : 'Submitting...')
      : (isEditMode ? 'Update Interview' : 'Submit Interview')
  }}
</span>
</button>
        </div>
</div>
      </main>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/services/api'
import HrSidebar from '../../components/hr/HrSidebar.vue'
import HrTopbar from '../../components/hr/HrTopbar.vue'

import { useToast } from 'vue-toastification'
const toast = useToast()
const isLoading = ref(false)

  import { usePermissions } from '@/composables/usePermissions'

const { hasPermission } = usePermissions()

const route = useRoute()
const applicantId = route.params.id

// Backend URL configuration
// const backendURL = "http://localhost:8000/api/"
  const backendURL = import.meta.env.VITE_API_URL;


const isSidebarOpen = ref(true)
const isDarkMode = ref(false)
const currentPageTitle = ref('First Interview')
const isSubmitting = ref(false)

// Applicant information
const applicantInfo = ref(null)
const interviewSchedule = ref(null)
const applicantCVs = ref([])

// Evaluation Criteria - Updated to handle your API response
const evaluationCriteria = ref([])
const evaluationRatings = ref({})
const evaluationComments = ref({})
const overallComments = ref('')
const recommendation = ref('')

// Questions
const mainQuestions = ref([])
const subQuestions = ref({})

// Answers object: { questionId: { answer: "", notes: "" } }
const answers = ref({})

// Add with your other refs
const existingInterview = ref(null); // Store fetched interview data
const isEditMode = ref(false); // Track if we're editing existing interview

// Helper function to get full file URL
const getFullFileUrl = (filePath) => {
  if (!filePath) return null
  
  // If it's already a full URL, return as is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath
  }
  
  // Remove any leading slashes to avoid double slashes
  const cleanPath = filePath.replace(/^\/+/, '')
  
  // Construct full URL
  return `${backendURL}${cleanPath}`
}

// ✅ Add this at the TOP LEVEL of <script setup>, alongside your other functions
const getFileIcon = (fileType) => {
  if (!fileType) return '📄';
  
  const type = fileType.toLowerCase();
  if (type.includes('pdf')) return '📕';
  if (type.includes('jpg') || type.includes('jpeg') || type.includes('png')) return '🖼️';
  if (type.includes('doc') || type.includes('docx')) return '📘';
  if (type.includes('photo')) return '📸';
  if (type.includes('txt')) return '📃';
  
  return '📄';
};

const goBack = () => {
  // Check if form has unsaved changes
  const hasChanges = hasSelectedRatings.value || 
                     overallComments.value || 
                     recommendation.value ||
                     Object.values(answers.value).some(a => a.answer || a.notes);
  
  if (hasChanges) {
    if (confirm('You have unsaved changes. Are you sure you want to leave?')) {
      window.history.back();
    }
  } else {
    window.history.back();
  }
};


const fetchExistingInterview = async () => {
  try {
    const response = await api.get(`GetInterviewAnswers.php?applicant_id=${applicantId}`);
    
    if (response.data?.status === 'success' && response.data?.data) {
      const data = response.data.data;
      existingInterview.value = data;
      isEditMode.value = true;
      
      // 🎯 Prefill Answers
      if (Array.isArray(data.answers)) {
        data.answers.forEach(ans => {
          if (answers.value[ans.question_id]) {
            answers.value[ans.question_id].answer = ans.answer || '';
            answers.value[ans.question_id].notes = ans.notes || '';
          }
        });
      }
      
      // 🎯 Prefill Evaluation
      if (data.evaluation) {
        // Ratings
        if (data.evaluation.criteria_ratings) {
          Object.entries(data.evaluation.criteria_ratings).forEach(([cid, rating]) => {
            if (evaluationRatings.value[cid] !== undefined) {
              evaluationRatings.value[cid] = rating;
            }
          });
        }
        // Criteria comments
        if (data.evaluation.criteria_comments) {
          Object.entries(data.evaluation.criteria_comments).forEach(([cid, comment]) => {
            if (evaluationComments.value[cid] !== undefined) {
              evaluationComments.value[cid] = comment;
            }
          });
        }
        // Overall
        overallComments.value = data.evaluation.overall_comments || '';
        recommendation.value = data.evaluation.recommendation || '';
      }
      
      console.log('✅ Interview prefill loaded');
    }
  } catch (err) {
    console.log('ℹ️ No existing interview (new interview)');
  }
};
// Fetch evaluation criteria - UPDATED for your API response
const fetchEvaluationCriteria = async () => {
  try {
    const response = await api.get('InterviewEvaluationCriteria.php')
    
    // Your API returns the criteria array directly, not wrapped in a success object
    if (Array.isArray(response.data)) {
      evaluationCriteria.value = response.data
      
      // Initialize ratings and comments objects for each criteria
      evaluationCriteria.value.forEach(criteria => {
        evaluationRatings.value[criteria.id] = ''
        evaluationComments.value[criteria.id] = ''
      })
      
      console.log('Evaluation criteria loaded:', evaluationCriteria.value)
    } else if (response.data && response.data.criteria) {
      // Fallback in case structure changes
      evaluationCriteria.value = response.data.criteria
      evaluationCriteria.value.forEach(criteria => {
        evaluationRatings.value[criteria.id] = ''
        evaluationComments.value[criteria.id] = ''
      })
    } else {
      console.error('Unexpected API response format:', response.data)
    }
  } catch (err) {
    console.error('Error fetching evaluation criteria:', err)
  }
}


// Computed property to check if any ratings are selected
const hasSelectedRatings = computed(() => {
  return Object.values(evaluationRatings.value).some(rating => rating !== '' && rating !== null)
})

// Generate rating options from 1 to max_score
const generateRatingOptions = (maxScore) => {
  const options = []
  for (let i = 1; i <= maxScore; i++) {
    options.push(i)
  }
  return options
}



// Fetch applicant information
const fetchApplicantInfo = async () => {
  try {
    const response = await api.get(`GetApplicantDetails.php?id=${applicantId}`)
    
    if (response.data && response.data.success) {
      // Extract data from the nested structure
      applicantInfo.value = response.data.data.applicant
      interviewSchedule.value = response.data.data.interview
      applicantCVs.value = response.data.data.cvs || []

      applicantInfo.value.attachments = response.data.attachments || [];

      
      console.log('Applicant loaded:', applicantInfo.value)
      console.log('Interview schedule:', interviewSchedule.value)
      console.log('CVs:', applicantCVs.value)
            console.log('Attachments loaded:', applicantInfo.value.attachments);

      
      // Log CV file paths for debugging
      if (applicantInfo.value.cv_file_path) {
        console.log('Main CV path:', applicantInfo.value.cv_file_path)
        console.log('Full CV URL:', getFullFileUrl(applicantInfo.value.cv_file_path))
      }
      
      if (applicantCVs.value.length > 0) {
        applicantCVs.value.forEach((cv, index) => {
          console.log(`CV ${index + 1} path:`, cv.file_path)
          console.log(`CV ${index + 1} full URL:`, getFullFileUrl(cv.file_path))
        })
      }
    } else {
      console.error('Failed to fetch applicant data:', response.data)
    }
  } catch (err) {
    console.error('Error fetching applicant info:', err)
  }
}

const fetchQuestions = async () => {
  try {
    const response = await api.get('InterviewQuestions.php')
    const questions = response.data

    // Separate main and subquestions
    mainQuestions.value = questions.filter(q => !q.parent_id)
    subQuestions.value = {}
    mainQuestions.value.forEach(main => {
      subQuestions.value[main.id] = questions.filter(q => q.parent_id === main.id)
    })

    // Initialize answers
    questions.forEach(q => {
      answers.value[q.id] = { answer: '', notes: '' }
    })

  } catch (err) {
    console.error('Error fetching questions:', err)
  }
}

// Calculate total score
const calculateTotalScore = () => {
  let total = 0
  let count = 0
  
  evaluationCriteria.value.forEach(criteria => {
    const rating = evaluationRatings.value[criteria.id]
    if (rating && !isNaN(parseInt(rating))) {
      total += parseInt(rating)
      count++
    }
  })
  
  return count > 0 ? total : null
}

// Calculate average score
const calculateAverageScore = () => {
  const total = calculateTotalScore()
  const count = evaluationCriteria.value.filter(criteria => 
    evaluationRatings.value[criteria.id] && !isNaN(parseInt(evaluationRatings.value[criteria.id]))
  ).length
  
  return count > 0 ? (total / count).toFixed(1) : null
}

// Submit answers
// const submitInterview = async () => {
//   isSubmitting.value = true;
//   try {
//     const payload = {
//       applicant_id: applicantId,
//       answers: answers.value,
//       interview_date: interviewSchedule.value?.interview_date,
//       evaluation: {
//         criteria_ratings: evaluationRatings.value,
//         criteria_comments: evaluationComments.value,
//         overall_comments: overallComments.value,
//         recommendation: recommendation.value,
//         total_score: calculateTotalScore(),
//         average_score: calculateAverageScore()
//       }
//     };
    
//     // ✅ Include interview_id if editing
//     if (isEditMode.value && existingInterview.value?.id) {
//       payload.interview_id = existingInterview.value.id;
//     }
    
//     const response = await api.post('SubmitInterview.php', payload);
    
//     if (response.data?.status === 'success') {
//       alert(isEditMode.value ? '✅ Interview updated!' : '✅ Interview submitted!');
//     } else {
//       alert('❌ ' + (response.data?.message || 'Submission failed'));
//     }
    
//   } catch (err) {
//     console.error(err);
//     alert('❌ Error submitting interview');
//   } finally {
//     isSubmitting.value = false;
//   }
// };

const submitInterview = async () => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;

  const MIN_LOADING_MS = 2000;
  const startTime = Date.now();

  const loadingMessage = isEditMode.value
    ? "Updating interview..."
    : "Submitting interview...";

  const toastId = toast.info(loadingMessage, { timeout: false });

  try {
    const payload = {
      applicant_id: applicantId,
      answers: answers.value,
      interview_date: interviewSchedule.value?.interview_date,
      evaluation: {
        criteria_ratings: evaluationRatings.value,
        criteria_comments: evaluationComments.value,
        overall_comments: overallComments.value,
        recommendation: recommendation.value,
        total_score: calculateTotalScore(),
        average_score: calculateAverageScore()
      }
    };

    // ✅ Include interview_id if editing
    if (isEditMode.value && existingInterview.value?.id) {
      payload.interview_id = existingInterview.value.id;
    }

    const response = await api.post('SubmitInterview.php', payload);

    // ✅ Handle backend error properly
    if (response.data?.status !== 'success') {
      throw new Error(response.data?.message || 'Submission failed');
    }

    // ✅ Success toast
    toast.success(
      isEditMode.value
        ? "Interview updated successfully!"
        : "Interview submitted successfully!"
    );

  } catch (err) {
    console.error(err);

    const errorMsg =
      err.response?.data?.message ||
      err.message ||
      "Error submitting interview";

    toast.error(errorMsg);

  } finally {
    // ✅ Minimum delay (smooth UX)
    const elapsed = Date.now() - startTime;
    const remaining = Math.max(0, MIN_LOADING_MS - elapsed);
    if (remaining > 0) await new Promise(res => setTimeout(res, remaining));

    // ✅ Dismiss loading toast
    toast.dismiss(toastId);

    isSubmitting.value = false;
  }
};

// CV handling functions with proper URL construction
const viewCV = (cvPath) => {
  if (cvPath) {
    const fullUrl = getFullFileUrl(cvPath)
    console.log('Opening CV from URL:', fullUrl)
    window.open(fullUrl, '_blank')
  } else {
    alert('CV file path not available')
  }
}

const downloadCV = (cvPath) => {
  if (cvPath) {
    const fullUrl = getFullFileUrl(cvPath)
    console.log('Downloading CV from URL:', fullUrl)
    
    // Create a hidden anchor element
    const link = document.createElement('a')
    link.href = fullUrl
    link.download = cvPath.split('/').pop() // Extract filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } else {
    alert('CV file path not available')
  }
}

// Helper functions
const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  } catch (e) {
    return dateString
  }
}

const getStatusColor = (status) => {
  const colors = {
    'New': 'text-green-600 dark:text-green-400',
    'Processing': 'text-yellow-600 dark:text-yellow-400',
    'Interviewed': 'text-blue-600 dark:text-blue-400',
    'Hired': 'text-purple-600 dark:text-purple-400',
    'Rejected': 'text-red-600 dark:text-red-400',
    'Pending': 'text-orange-600 dark:text-orange-400'
  }
  return colors[status] || 'text-gray-600 dark:text-gray-400'
}

// Dark mode & Sidebar
const toggleSidebar = () => isSidebarOpen.value = !isSidebarOpen.value
const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
  document.documentElement.classList.toggle('dark', isDarkMode.value)
}

onMounted(() => {
  fetchApplicantInfo()
  fetchQuestions()
  fetchEvaluationCriteria()
  fetchExistingInterview() 
})
</script>